import React from 'react'
import { cn } from '@/utils'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  leftElement?: React.ReactNode
  rightElement?: React.ReactNode
  error?: string
  variant?: 'default' | 'search'
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, leftElement, rightElement, error, variant = 'default', ...props }, ref) => {
    // Вариант для поля поиска из Figma
    if (variant === 'search') {
      return (
        <div className="relative w-full">
          <div className={cn(
            'flex items-center gap-[8px]',
            'bg-[rgba(255,255,255,0.52)] dark:bg-transparent',
            'border border-[rgba(45,41,35,0.12)] dark:border-[rgba(255,255,255,0.20)]',
            'px-[12px] py-[18px]',
            'w-full rounded-md',
            'hover:border-[rgba(45,41,35,0.12)] dark:hover:border-[rgba(255,255,255,0.28)]',
            'focus-within:border-[rgba(45,41,35,0.12)] dark:focus-within:border-[rgba(255,255,255,0.32)]'
          )}>
            {leftElement && (
              <div className="shrink-0 w-4 h-4 flex items-center justify-center">
                {leftElement}
              </div>
            )}
            <input
              type={type}
              className={cn(
                'flex-1 min-w-0',
                'bg-transparent border-0 outline-none',
                'font-plus-jakarta font-normal leading-[18px] text-[13px]',
                'text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]',
                'placeholder:text-[rgba(45,41,35,0.48)] dark:placeholder:text-[rgba(253,253,253,0.48)]',
                'focus:outline-none',
                'disabled:cursor-not-allowed disabled:opacity-50',
                className
              )}
              ref={ref}
              {...props}
            />
            {rightElement && (
              <div className="shrink-0 flex items-center justify-center">
                {rightElement}
              </div>
            )}
          </div>
          {error && (
            <p className="mt-1 text-sm text-red-500">{error}</p>
          )}
        </div>
      )
    }

    // Стандартный вариант
    return (
      <div className="relative">
        {leftElement && (
          <div className="absolute left-[12px] top-1/2 -translate-y-1/2 flex items-center">
            {leftElement}
          </div>
        )}
        <input
          type={type}
          className={cn(
            'flex h-12 w-full rounded-lg border border-border bg-background',
            'pl-[12px] pr-[18px] py-[14px] text-sm text-foreground placeholder:text-muted-foreground',
            'focus:outline-none focus:ring-0 focus:border-border transition-colors',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'hover:border-border',
            leftElement && 'pl-[42px]',
            rightElement && 'pr-[42px]',
            error && 'border-destructive focus:border-destructive focus:ring-destructive/20',
            className
          )}
          ref={ref}
          {...props}
        />
        {rightElement && (
          <div className="absolute right-[18px] top-1/2 -translate-y-1/2 flex items-center justify-center h-5">
            {rightElement}
          </div>
        )}
        {error && (
          <p className="mt-1 text-sm text-red-500">{error}</p>
        )}
      </div>
    )
  }
)
Input.displayName = 'Input'

export default Input 